'use client';

import { useEffect, useRef, useState } from 'react';
import { FiFileText, FiArrowUpRight } from 'react-icons/fi';
import AppLink from './AppLink';
import { fetchLatestLandingJob, useLandingAccount } from '../../lib/landingAccount';
import './LatestTranscript.css';

const messages = {
  en: { title: 'Your latest transcription', completed: 'Ready', preview: '3-minute preview ready',
    running: 'Transcription in progress', queued: 'Waiting to transcribe', uploaded: 'Ready to start',
    upgrade: 'Upgrade required', failed: 'Needs attention', unknown: 'Saved recording',
    open: 'Open transcript', progress: 'View progress', resume: 'Continue', review: 'View job', fallback: 'Your recording',
    stale: 'Couldn’t refresh. Showing your last saved status.', unavailable: 'Your latest transcription could not be loaded.', retry: 'Try again', refreshing: 'Refreshing…' },
  de: { title: 'Deine letzte Transkription', completed: 'Fertig', preview: '3-Minuten-Vorschau fertig',
    running: 'Transkription läuft', queued: 'Wartet auf Transkription', uploaded: 'Noch nicht gestartet',
    upgrade: 'Upgrade erforderlich', failed: 'Aktion erforderlich', unknown: 'Gespeicherte Aufnahme',
    open: 'Transkript öffnen', progress: 'Fortschritt ansehen', resume: 'Fortsetzen', review: 'Auftrag ansehen', fallback: 'Deine Aufnahme',
    stale: 'Aktualisierung fehlgeschlagen. Zuletzt gespeicherter Stand wird angezeigt.', unavailable: 'Deine letzte Transkription konnte nicht geladen werden.', retry: 'Erneut versuchen', refreshing: 'Wird aktualisiert…' },
  fr: { title: 'Votre dernière transcription', completed: 'Terminée', preview: 'Aperçu de 3 minutes prêt',
    running: 'Transcription en cours', queued: 'En attente de transcription', uploaded: 'Prête à démarrer',
    upgrade: 'Offre Premium requise', failed: 'Action nécessaire', unknown: 'Enregistrement sauvegardé',
    open: 'Ouvrir la transcription', progress: 'Voir la progression', resume: 'Continuer', review: 'Voir la tâche', fallback: 'Votre enregistrement',
    stale: 'Actualisation impossible. Dernier état sauvegardé affiché.', unavailable: 'Votre dernière transcription n’a pas pu être chargée.', retry: 'Réessayer', refreshing: 'Actualisation…' },
};

function jobState(job) {
  const status = String(job.status || '').toLowerCase();
  if (status === 'completed' || status === 'succeeded') return job.is_preview ? 'preview' : 'completed';
  if (status === 'processing' || status === 'running') return 'running';
  if (['transcribing', 'queued', 'starting'].includes(status)) return 'queued';
  if (status === 'uploaded') return 'uploaded';
  if (status === 'requires_upgrade') return 'upgrade';
  if (status.startsWith('error') || ['failed', 'cancelled', 'canceled'].includes(status)) return 'failed';
  return 'unknown';
}

export default function LatestTranscript({ locale = 'en' }) {
  const account = useLandingAccount();
  const userId = account?.id;
  const [saved, setSaved] = useState(null);
  const [refreshState, setRefreshState] = useState(null);
  const refreshRef = useRef(null);
  const copy = messages[locale] || messages.en;
  useEffect(() => {
    if (!userId) return;
    let disposed = false;
    let request;
    let timer;
    let failures = 0;
    const refresh = async () => {
      if (disposed || document.visibilityState === 'hidden') return;
      clearTimeout(timer);
      request?.abort('replaced');
      const controller = new AbortController();
      request = controller;
      setRefreshState(previous => previous?.userId === userId && previous.error ? { ...previous, loading: true } : null);
      try {
        const job = await fetchLatestLandingJob(userId, controller.signal);
        if (disposed || controller.signal.aborted) return;
        setSaved({ userId, job });
        setRefreshState(null);
        failures = 0;
        const state = job && jobState(job);
        timer = setTimeout(refresh, ['running', 'queued'].includes(state) ? 15000 : 60000);
      } catch (error) {
        if (!disposed && !controller.signal.aborted) {
          // Clear private data for verified denial; keep owned data during an
          // outage. The userId guard also hides it on sign-out/account changes.
          if ([401,403].includes(error.status)) setSaved(null);
          setRefreshState({ userId, error: true, loading: false });
          failures += 1;
          timer = setTimeout(refresh, failures <= 2 && ![401,403].includes(error.status) ? 5000 : 60000);
        }
      }
    };
    refreshRef.current = refresh;
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    refresh();
    return () => {
      disposed = true; clearTimeout(timer); request?.abort('account_changed'); refreshRef.current = null;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [userId]);
  const job = saved && saved.userId === userId ? saved.job : null;
  const refreshStatus = refreshState?.userId === userId ? refreshState : null;
  if (!userId || (!job?.id && !refreshStatus?.error)) return null;
  const state = job ? jobState(job) : 'unknown';
  const action = ['completed', 'preview'].includes(state) ? copy.open
    : ['running', 'queued'].includes(state) ? copy.progress
      : ['uploaded', 'upgrade'].includes(state) ? copy.resume : copy.review;
  const params = new URLSearchParams({ lang: locale, ...(job?.id ? { record: job.id } : {}) });
  return <article className="landing-latest-job" aria-label={copy.title}>
    <div className="landing-latest-job__heading"><FiFileText aria-hidden="true" /><span>{copy.title}</span></div>
    {job?.id && <strong className="landing-latest-job__filename" title={job.original_filename || copy.fallback}>{job.original_filename || copy.fallback}</strong>}
    {job?.id && <div className="landing-latest-job__footer">
      <span className={`landing-latest-job__status landing-latest-job__status--${state}`} role="status">{copy[state]}</span>
      <AppLink href={`/app/dashboard?${params}`} data-analytics-cta-id="guest_entry_latest_job"
        data-analytics-cta-location="guest_entry">{action}<FiArrowUpRight aria-hidden="true" /></AppLink>
    </div>}
    {refreshStatus?.error && <div className="landing-latest-job__refresh" role="status">
      <span>{refreshStatus.loading ? copy.refreshing : job?.id ? copy.stale : copy.unavailable}</span>
      <button type="button" disabled={refreshStatus.loading} onClick={() => refreshRef.current?.()}>{copy.retry}</button>
    </div>}
  </article>;
}
